import { LOADING_STATUSES } from '@yandex-int/k-common/client/api/constants';
import { CLESSON_MODES } from '@yandex-int/k-common/constants';
import { LESSON_STATUS } from '@yandex-int/k-common/typings';
import b from 'b_';
import cx from 'classnames';
import { addMinutes, differenceInMinutes, isEqual } from 'date-fns';
import { isNil } from 'lodash';
import { Spin } from 'platform-components';
import { getMe } from 'platform-components/src/components/user/selectors';
import { type InjectedProps as WithRouterPathProps, withRouterPath } from 'platform-components/hocs';
import { reportMessage } from 'platform-components/utils';
import React from 'react';
import { type WrappedComponentProps, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import ym from 'react-yandex-metrika';
import { type Action, bindActionCreators, type Dispatch } from 'redux';

import * as classSelectors from 'common.components/classes/selectors';
import { LESSON_DATES, LIBRARY_CONTENT_TYPE } from 'common.components/constants';
import { getCurrentYearCourseId } from 'common.components/course/selectors';
import { actions as lessonActions } from 'common.components/lesson';
import * as lessonEditorSelectors from 'common.components/lesson/selectors';
import { getLessonDefaultName } from 'common.components/lesson/utils';
import { STUDENTS_ERRORS } from 'common.components/students-list/typings';
import { teamBuilderActions } from 'common.components/team-builder/actions';
import { TEAM_BUILDER_ENTRY_STEPS } from 'common.components/team-builder/typings';
import withTime, { type InjectedProps as WithNowProps } from 'common.components/timer/withTime';
import type { Student } from 'common.components/typings';
import * as userActions from 'common.components/user/actions';
import * as userSelectors from 'common.components/user/selectors';
import { getIsStudentRegistrationLinkAvailableInTeam } from 'common.components/user/selectors';
import { getIsTeacherFromMes } from 'utils/external-lms/external-lms';
import { getIsLessonEvaluable } from 'utils/lesson';
import { YM_LOCATION } from 'utils/ym-constants';

import { ExternalLmsNotice } from './__external-lms-notice/assignment__external-lms-notice';
import { AssignmentMainSettingsContainer } from './__main-settings/assignment__main-settings.container';
import { AssignmentMethodologicalGoal } from './__methodological-goal/assignment__methodological-goal';
import * as actions from './actions';
import * as selectors from './selectors';
import { ACCORDION_ITEMS, ASSIGNMENT_STATES } from './typings';

import './assignment.scss';

type StoreProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;

interface OwnProps extends WrappedComponentProps, WithRouterPathProps, WithNowProps {
    shouldFillDates?: boolean;
}

export interface Props extends OwnProps, StoreProps, DispatchProps {}

// assignmentTable - это черный список учеников
const getSelectedStudents = (
    students: Array<Student>,
    assignmentTable: Array<string>,
    canAssignIndividually: boolean
): Array<number> => {
    if (!canAssignIndividually) {
        if (assignmentTable.length !== 0) {
            reportMessage('Forbidden individual assignment occurred.');

            return students.map((s: Student) => s.id);
        }
    }

    return students.filter((s: Student) => assignmentTable.indexOf(s.id.toString()) === -1).map((s: Student) => s.id);
};

const MAX_DURATION = 999;
const MIN_DURATION = 5;
const MIN_PREPARATION_TIME = 15;

export class Assignment extends React.Component<Props> {
    componentDidMount() {
        const {
            isTeacherFromMes,
            students,
            assignmentTable,
            setAssignDate,
            setDeadlineDate,
            canAssignIndividually,
            setSelectedStudents,
            setAccordionItem,
            setAssignmentState,
        } = this.props;

        setAssignmentState(ASSIGNMENT_STATES.IDLE);
        setAccordionItem(ACCORDION_ITEMS.DATES);
        this.clearErrorMessages();

        const selectedStudents = students ? getSelectedStudents(students, assignmentTable, canAssignIndividually) : [];

        setSelectedStudents(selectedStudents);

        if (isTeacherFromMes) {
            // Датой выдачи учитель управляет из МЭШ, мы ставим максимально широкую дату
            setAccordionItem(null);

            setAssignDate({
                type: LESSON_DATES.AFTER_ASSIGN,
                customDate: null,
            });
            setDeadlineDate({
                type: LESSON_DATES.NONE,
                customDate: null,
            });
        } else {
            this.setDefaultDates();
            this.setDefaultLessonMode();
        }
    }

    // eslint-disable-next-line complexity
    componentDidUpdate(prevProps: Props) {
        const { assignmentState: prevAssignmentState, students: prevStudents } = prevProps;
        const {
            createLessonLoadingStatus,
            assignmentState,
            assignmentTable,
            canAssignIndividually,
            invalidAccordionItem,
            lessonLoadingStatus,
            setAccordionItem,
            setSelectedStudents,
            setStudentsError,
            students,
        } = this.props;

        const wasLessonLoading = lessonLoadingStatus === LOADING_STATUSES.LOADING;
        const isLessonLoaded = lessonLoadingStatus === LOADING_STATUSES.SUCCESS;
        const isLessonCreated = createLessonLoadingStatus === LOADING_STATUSES.SUCCESS;
        const isStudentsLoaded = prevStudents?.length !== students?.length || (!prevStudents && students);

        if (isStudentsLoaded) {
            setStudentsError(null);

            if (prevStudents?.length === 0) {
                setAccordionItem(ACCORDION_ITEMS.STUDENTS);
            }
        }

        if ((isLessonLoaded && wasLessonLoading) || ((isLessonLoaded || isLessonCreated) && isStudentsLoaded)) {
            const selected = students && getSelectedStudents(students, assignmentTable, canAssignIndividually);

            if (selected) {
                setSelectedStudents(selected);
            }
        }

        if (assignmentState === ASSIGNMENT_STATES.VALIDATING && prevAssignmentState !== assignmentState) {
            this.validate();
        }

        if (assignmentState === ASSIGNMENT_STATES.ERROR && prevAssignmentState !== assignmentState) {
            setAccordionItem(invalidAccordionItem);
        }
    }

    componentWillUnmount() {
        this.clear();
    }

    clear = () => {
        const { setAssignDate, setDeadlineDate, setDuration, setLessonMode, setSelectedStudents } = this.props;
        this.clearErrorMessages();
        setAssignDate(null);
        setDeadlineDate(null);
        setDuration(null);
        setLessonMode(null);
        setSelectedStudents([]);
    };

    // eslint-disable-next-line complexity
    setDefaultDates = () => {
        const {
            assignDate,
            deadlineDate,
            shouldGetAssignmentDatesFromMeta,
            shouldGetLessonModeFromMeta,
            clesson,
            setAssignDate,
            setDeadlineDate,
            setDuration,
            shouldFillDates,
        } = this.props;

        const assignDateStr = shouldGetAssignmentDatesFromMeta
            ? clesson?.meta.accessible_to_teacher
            : clesson?.clesson?.date_assignment;
        const deadlineDateStr = shouldGetAssignmentDatesFromMeta
            ? clesson?.meta.date_deadline
            : clesson?.clesson?.date_deadline;
        const duration = shouldGetAssignmentDatesFromMeta ? clesson?.meta.duration : clesson?.clesson?.duration;
        const lessonMode = shouldGetLessonModeFromMeta ? clesson?.meta.mode : clesson?.clesson?.mode;

        if (shouldFillDates) {
            setAssignDate({
                type: LESSON_DATES.AFTER_ASSIGN,
                customDate: null,
            });
            setDeadlineDate({
                type: LESSON_DATES.NONE,
                customDate: null,
            });

            return;
        }

        if (assignDateStr) {
            setAssignDate({
                type: LESSON_DATES.CUSTOM,
                customDate: new Date(assignDateStr),
            });
        } else if (lessonMode !== CLESSON_MODES.CONTROL_WORK && !assignDate) {
            // для не КР нужно заполнить поле ученики приступят — сразу после выдачи
            setAssignDate({
                type: LESSON_DATES.AFTER_ASSIGN,
                customDate: null,
            });
        } else {
            setAssignDate(null);
        }

        if (deadlineDateStr) {
            const deadlineCustomDate = addMinutes(new Date(deadlineDateStr), duration ? -duration : 0);
            setDeadlineDate({
                type: LESSON_DATES.CUSTOM,
                customDate: deadlineCustomDate,
            });
        } else if (lessonMode !== CLESSON_MODES.CONTROL_WORK && !deadlineDate) {
            // для не КР нужно заполнить поле ученики должны сдать — без ограничения по времени
            setDeadlineDate({
                type: LESSON_DATES.NONE,
                customDate: null,
            });
        } else {
            setDeadlineDate(null);
        }

        if (duration) {
            setDuration(duration);
        } else {
            setDuration(null);
        }
    };

    setDefaultLessonMode = () => {
        const { shouldGetLessonModeFromMeta, clesson, setLessonMode } = this.props;

        const lessonMode = shouldGetLessonModeFromMeta ? clesson?.meta.mode : clesson?.clesson?.mode;

        setLessonMode(lessonMode || null);
    };

    handleToggleStudentAssignment = (selected: Array<number>) => {
        const {
            clesson,
            isPreparedLesson,
            students,
            selectedStudents: prevSelectedStudents,
            setSelectedStudents,
            setStudentsError,
            activeSubscriptionSlug,
        } = this.props;

        setSelectedStudents(selected);
        setStudentsError(null);

        const metrikaParams = {
            lessonId: clesson?.id,
            clessonId: clesson?.clesson?.id,
            contentType: isPreparedLesson ? LIBRARY_CONTENT_TYPE.PREPARED_LESSON : LIBRARY_CONTENT_TYPE.COURSE_LESSON,
            subscription_slug: activeSubscriptionSlug,
        };

        if (students?.length === selected.length) {
            ym('reachGoal', 'teacher_lesson_creation_all_assignment', metrikaParams);
        } else if (students?.length === prevSelectedStudents.length) {
            ym('reachGoal', 'teacher_lesson_creation_individual_assignment', metrikaParams);
        }
    };

    clearErrorMessages = () => {
        const {
            setAssignDateError,
            setDeadlineDateError,
            setDurationError,
            setStudentsError,
            setExternalLmsDatesError,
        } = this.props;

        setAssignDateError(null);
        setDeadlineDateError(null);
        setDurationError(null);
        setStudentsError(null);
        setExternalLmsDatesError(null);
    };

    selectMode = (mode: CLESSON_MODES) => {
        const {
            clesson,
            isPreparedLesson,
            areDatesEditable,
            intl,
            setLessonMode,
            setName,
            match: {
                params: { subject },
            },
            setAssignDate,
            setDeadlineDate,
            isLessonNamedByDefault,
            lessonStatus,
            assignDate,
            deadlineDate,
            activeSubscriptionSlug,
        } = this.props;

        // меняем имя только для черновика, для опубликованного при сохранении всех настроек
        if (isLessonNamedByDefault && lessonStatus === LESSON_STATUS.NOT_ASSIGNED) {
            setName({ lessonName: getLessonDefaultName({ intl, isEvaluable: getIsLessonEvaluable(mode) }) });
        }

        setLessonMode(mode);
        this.clearErrorMessages();

        if (areDatesEditable) {
            setAssignDate(null);
            setDeadlineDate(null);
        }

        if (mode !== CLESSON_MODES.CONTROL_WORK) {
            // для не КР нужно заполнить поле ученики приступят — сразу после выдачи
            if (!assignDate) {
                setAssignDate({
                    type: LESSON_DATES.AFTER_ASSIGN,
                    customDate: null,
                });
            }
            if (!deadlineDate) {
                setDeadlineDate({
                    type: LESSON_DATES.NONE,
                    customDate: null,
                });
            }
        }

        ym('reachGoal', 'teacher_lesson_creation_chose_type', {
            clessonId: clesson?.clesson?.id,
            subject,
            contentType: isPreparedLesson ? 'preparedLesson' : 'courseLesson',
            mode,
            subscription_slug: activeSubscriptionSlug,
        });
    };

    // eslint-disable-next-line complexity
    validate = (): void => {
        const { me, setAssignmentState, activeSubscriptionSlug } = this.props;

        const studentsError = this.validateStudents();
        const { assignDateError, deadlineDateError, durationError } = this.validateDates();

        const isValidTeam = studentsError === null;
        const isValidDates = !assignDateError && !deadlineDateError && !durationError;

        if (isValidTeam && isValidDates) {
            setAssignmentState(ASSIGNMENT_STATES.SUBMITTING);
        } else {
            const errorList = [
                assignDateError && 'time',
                deadlineDateError && 'deadline',
                durationError && 'duration',
                studentsError === STUDENTS_ERRORS.EMPTY_TEAM && 'no_students',
                studentsError === STUDENTS_ERRORS.EMPTY_SCHOOL_AND_TEAM && 'no_school_and_students',
                studentsError === STUDENTS_ERRORS.NO_STUDENTS_SELECTED && 'individual_assignment',
            ].filter(Boolean);
            ym('reachGoal', 'teacher_lesson_creation_validation_failed', {
                error_list: errorList,
                user_id: me?.id,
                subscription_slug: activeSubscriptionSlug,
            });

            setAssignmentState(ASSIGNMENT_STATES.ERROR);
        }
    };

    // eslint-disable-next-line complexity
    validateStudents = (): STUDENTS_ERRORS | null => {
        const { students, selectedStudents, setStudentsError, schoolId, isInStudentLinkExperiment } = this.props;

        let error: STUDENTS_ERRORS | null = null;

        if (isInStudentLinkExperiment) {
            if (!schoolId) {
                error = STUDENTS_ERRORS.EMPTY_SCHOOL;
            }
        } else {
            // старый флоу, где отсутствие учеников вызывает ошибки
            // eslint-disable-next-line no-lonely-if
            if (!schoolId || !students) {
                error = STUDENTS_ERRORS.EMPTY_SCHOOL_AND_TEAM;
            } else if (students && students.length === 0) {
                error = STUDENTS_ERRORS.EMPTY_TEAM;
            } else if (selectedStudents.length === 0) {
                error = STUDENTS_ERRORS.NO_STUDENTS_SELECTED;
            }
        }

        setStudentsError(error);

        return error;
    };

    // eslint-disable-next-line complexity
    getAssignDateError = () => {
        const {
            intl,
            clesson,
            areDatesEditable,
            assignDate,
            assignDateValue,
            deadlineDate,
            deadlineDateValue,
            lessonMode,
        } = this.props;
        const nowDate = new Date();
        const isControlWork = lessonMode === CLESSON_MODES.CONTROL_WORK;

        if (!assignDate?.type || (assignDate.type === LESSON_DATES.CUSTOM && !assignDate.customDate)) {
            return isControlWork
                ? intl.formatMessage({ id: 'assignLessonModal.errors.controlWork.noAssignDate' })
                : intl.formatMessage({ id: 'assignLessonModal.errors.noAssignDate' });
        }

        if (
            assignDate.type === LESSON_DATES.CUSTOM &&
            assignDate?.customDate &&
            assignDate?.customDate < nowDate &&
            areDatesEditable
        ) {
            //@ts-ignore
            const prevValue = new Date(clesson?.clesson?.date_assignment);
            const isAssignDateChanged = !isEqual(prevValue, assignDate?.customDate);

            if (isAssignDateChanged) {
                return isControlWork
                    ? intl.formatMessage({ id: 'assignLessonModal.errors.controlWork.assignDateInPast' })
                    : intl.formatMessage({ id: 'assignLessonModal.errors.assignDateInPast' });
            }
        }

        if (!isNil(deadlineDate)) {
            if (deadlineDateValue && assignDateValue && assignDateValue > deadlineDateValue) {
                return isControlWork
                    ? intl.formatMessage({ id: 'assignLessonModal.errors.controlWork.incompatibleDates' })
                    : intl.formatMessage({ id: 'assignLessonModal.errors.incompatibleDateAssign' });
            }
        }

        return null;
    };

    // eslint-disable-next-line complexity
    getDeadlineDateError = () => {
        const { intl, assignDate, assignDateValue, deadlineDate, deadlineDateValue, lessonMode } = this.props;
        const nowDate = new Date();
        const isControlWork = lessonMode === CLESSON_MODES.CONTROL_WORK;

        if (!deadlineDate?.type || (deadlineDate.type === LESSON_DATES.CUSTOM && !deadlineDate.customDate)) {
            return isControlWork
                ? intl.formatMessage({ id: 'assignLessonModal.errors.controlWork.noDeadlineDate' })
                : intl.formatMessage({ id: 'assignLessonModal.errors.noDeadlineDate' });
        }

        if (deadlineDate.type === LESSON_DATES.CUSTOM && deadlineDate?.customDate! < nowDate) {
            return isControlWork
                ? intl.formatMessage({ id: 'assignLessonModal.errors.controlWork.deadlineDateInPast' })
                : intl.formatMessage({ id: 'assignLessonModal.errors.assignDateInPast' });
        }

        if (isControlWork && assignDate?.type) {
            if (
                assignDateValue &&
                deadlineDateValue &&
                differenceInMinutes(deadlineDateValue, assignDateValue) < MIN_PREPARATION_TIME
            ) {
                return intl.formatMessage({ id: 'assignLessonModal.errors.controlWork.incompatibleDateDeadline' });
            }
        }

        return null;
    };

    getDurationError = () => {
        const { intl, duration, lessonMode, isDurationEditable } = this.props;

        if (lessonMode !== CLESSON_MODES.CONTROL_WORK || !isDurationEditable) {
            return null;
        }

        if (isNil(duration)) {
            return intl.formatMessage({ id: 'assignLessonModal.errors.noDuration' });
        }

        if (duration > MAX_DURATION) {
            return intl.formatMessage({ id: 'assignLessonModal.errors.durationMaxValue' });
        }

        if (duration < MIN_DURATION) {
            return intl.formatMessage({ id: 'assignLessonModal.errors.durationMinValue' });
        }

        return null;
    };

    validateDates = () => {
        const { setAssignDateError, setDeadlineDateError, setDurationError } = this.props;

        const assignDateError = this.getAssignDateError();
        const deadlineDateError = this.getDeadlineDateError();
        const durationError = this.getDurationError();

        setAssignDateError(assignDateError);
        setDeadlineDateError(deadlineDateError);
        setDurationError(durationError);

        return { assignDateError, deadlineDateError, durationError };
    };

    handleAddStudentsClick = () => {
        const { me, startTeamBuilder, teamId, students, finishAddingStudentsRequest } = this.props;

        ym('reachGoal', 'teacher_lesson_creation_add_new_student', {
            user_id: me!.id,
            team_id: teamId,
            students_count: Array.isArray(students) ? students.length : 0,
        });

        startTeamBuilder({
            entryPoint: TEAM_BUILDER_ENTRY_STEPS.STUDENTS,
            onSuccess: (studentsIds: Array<number>) =>
                finishAddingStudentsRequest({ studentsIds, ymLocation: YM_LOCATION.LESSON_VIEW }),
        });
    };

    handleSendStudentLinkClick = () => {
        const { startTeamBuilder } = this.props;

        startTeamBuilder({
            entryPoint: TEAM_BUILDER_ENTRY_STEPS.STUDENT_LINK,
            location: YM_LOCATION.LESSON_VIEW,
        });
    };

    handleAccordionItemClick = (id: ACCORDION_ITEMS) => {
        const { currentAccordionItem, setAccordionItem, setWizardVariables } = this.props;

        setAccordionItem(id === currentAccordionItem ? null : id);

        const wizardVariableId = id
            .replace(/(^.)/g, (v) => v.toUpperCase()) // апперкейсим первую букву
            .replace(/_(\w)/g, (v) => v.toUpperCase()) // апперкейсим первую букву после _
            .replace(/_/g, '');
        setWizardVariables({ [`accordionItem${wizardVariableId}Clicked`]: true });
    };

    render() {
        const {
            activeSubscriptionSlug,
            createLessonLoadingStatus,
            lessonLoadingStatus,
            shouldShowLmsLoader,
            teamId,
            methodologicalGoal,
            isTeacherFromMes,
        } = this.props;

        const isLessonLoading = lessonLoadingStatus === LOADING_STATUSES.LOADING;
        const isLessonCreating = createLessonLoadingStatus === LOADING_STATUSES.LOADING;

        if (isLessonLoading || isLessonCreating) {
            return <Spin center mix="assignment__spin" />;
        }

        return (
            <>
                <div className={cx(b('assignment', { loading: shouldShowLmsLoader }), 'lesson-view__assignment')}>
                    {isTeacherFromMes && <ExternalLmsNotice />}
                    <AssignmentMethodologicalGoal methodologicalGoal={methodologicalGoal} />
                    <AssignmentMainSettingsContainer
                        activeSubscriptionSlug={activeSubscriptionSlug}
                        onAccordionItemClick={this.handleAccordionItemClick}
                        onAddStudentsClick={this.handleAddStudentsClick}
                        onSelectMode={this.selectMode}
                        onSendStudentLinkClick={this.handleSendStudentLinkClick}
                        onToggleStudentAssignment={this.handleToggleStudentAssignment}
                        teamId={teamId}
                    />
                </div>
            </>
        );
    }
}

const mapStateToProps = (storeState: any, ownProps: OwnProps) => {
    const {
        now,
        intl,
        match,
        location: { search },
    } = ownProps;

    const classId = Number(match.params.classId);

    const lesson = lessonEditorSelectors.getLesson(storeState)!;
    const courseId = getCurrentYearCourseId(storeState);

    return {
        assignDate: selectors.getAssignDate(storeState),
        assignDateValue: selectors.getAssignDateValue(storeState, { now }),
        assignmentState: selectors.getAssignmentState(storeState),
        assignmentTable: Object.keys(lessonEditorSelectors.getAssignmentTable(storeState)),
        teamId: classId,
        clesson: lesson,
        currentAccordionItem: selectors.getCurrentAccordionItem(storeState),
        deadlineDate: selectors.getDeadlineDate(storeState),
        deadlineDateValue: selectors.getDeadlineDateValue(storeState, { now }),
        duration: selectors.getDuration(storeState),
        invalidAccordionItem: selectors.getInvalidAccordionItem(storeState),
        canAssignIndividually: lessonEditorSelectors.getCanAssignIndividually(storeState),
        isInStudentLinkExperiment: getIsStudentRegistrationLinkAvailableInTeam(storeState, { classId }),
        isLessonNamedByDefault: lessonEditorSelectors.getIsLessonNamedByDefault(storeState, { intl }),
        isPreparedLesson: lessonEditorSelectors.getIsPreparedLesson(storeState),
        areDatesEditable: lessonEditorSelectors.getCanEditAssignmentDates(storeState),
        lessonMode: selectors.getLessonMode(storeState) || CLESSON_MODES.LESSON,
        lessonStatus: lessonEditorSelectors.getLessonStatus(storeState) || LESSON_STATUS.NOT_ASSIGNED,
        lessonLoadingStatus: lessonEditorSelectors.getLessonLoadingStatus(storeState),
        me: getMe(storeState),
        methodologicalGoal: lesson?.meta?.methodological_goal,
        selectedStudents: selectors.getSelectedStudents(storeState),
        schoolId: userSelectors.getSchoolId(storeState),
        shouldGetAssignmentDatesFromMeta: lessonEditorSelectors.getShouldGetAssignmentDatesFromMeta(storeState),
        shouldGetLessonModeFromMeta: lessonEditorSelectors.getShouldGetLessonModeFromMeta(storeState),
        students: classSelectors.getStudents(storeState, { classId }),
        studentsError: selectors.getSelectedStudentsError(storeState),
        createLessonLoadingStatus: lessonEditorSelectors.getCreateLessonStatus(storeState),
        activeSubscriptionSlug: lessonEditorSelectors.getLessonActiveSubscriptionSlug(storeState, { courseId }),
        shouldShowLmsLoader: selectors.getShouldShowLmsLoader(storeState, { classId }),
        isDurationEditable: lessonEditorSelectors.getIsDurationEditable(storeState),
        isTeacherFromMes: getIsTeacherFromMes(search),
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>) =>
    bindActionCreators(
        {
            finishAddingStudentsRequest: lessonActions.finishAddingStudentsRequest,
            setAccordionItem: actions.setAccordionItem,
            setAssignDate: actions.setAssignDate,
            setAssignDateError: actions.setAssignDateError,
            setAssignmentState: actions.setAssignmentState,
            setDeadlineDate: actions.setDeadlineDate,
            setDeadlineDateError: actions.setDeadlineDateError,
            setDuration: actions.setDuration,
            setDurationError: actions.setDurationError,
            setLessonMode: actions.setLessonMode,
            setName: lessonActions.setName,
            setSelectedStudents: actions.setSelectedStudents,
            setStudentsError: actions.setStudentsError,
            setWizardVariables: userActions.setWizardVariables,
            startTeamBuilder: teamBuilderActions.start,
            setExternalLmsDatesError: actions.setExternalLmsDatesError,
        },
        dispatch
    );

const container = injectIntl(withTime(withRouterPath(connect(mapStateToProps, mapDispatchToProps)(Assignment))));

container.displayName = 'LessonAssignment';

export default container;
